import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Check, Plus, UserPlus, Sparkles, Pencil, ArrowLeft, ArrowRight, Info,
  LayoutGrid, Clapperboard, Rocket, LineChart, GraduationCap, BookOpen, Megaphone, Search, Code, TrendingUp, Heart,
  HeartPulse, Wallet, Scale, Briefcase, Kanban, ChefHat, Plane, Compass,
} from 'lucide-react';
import { getAgentProfiles } from '../../services/agentService';
import { listCustomAgents, type CustomAgent } from '../../services/customAgentService';
import { SpecialistIcon } from './specialistIcons';
import MediaCrewPicker from './MediaCrewPicker';
import { useCatalogAvatars } from './useCatalogAvatars';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { usePlan } from '../../contexts/PlanContext';
import { toMember, suggestTeam } from './teamSuggest';
import AgentEditorModal from './AgentEditorModal';
import AgentPreviewCard from './AgentPreviewCard';
import { flowOverlay, flowPanel, modalHeader, modalTitle, modalField } from '../ui/modalSpacing';
import { GROUP_MIN, GROUP_MAX, type Specialist, type MemberSpec } from './types';
import type { ProactiveLevel } from '../../services/conversationService';
import GroupAvatar from './GroupAvatar';
import { TYPE } from '../../constants/typeScale';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (members: MemberSpec[], name?: string, proactiveLevel?: ProactiveLevel) => void;
}

/**
 * The single proactivity dial offered at creation — drives ambient news + agent-to-agent chatter.
 *
 * Only None and Occasional are offered (owner decision 2026-08-13). Collaborative and Vibrant both spend
 * credits on agents talking to EACH OTHER, which is not what people come here to pay for; they remain in
 * `ProactiveLevel` so existing groups keep working, but a new crew can no longer be started on one.
 * A new crew defaults to None — a quiet room, nothing spent, until the creator asks for more.
 */
const PROACTIVE_OPTIONS: { key: ProactiveLevel; label: string; caption: string }[] = [
  { key: 'none', label: 'None', caption: 'A quiet room. Agents stay silent until you message them and never start a thread on their own — no background news, no cross-talk, no credits spent.' },
  { key: 'occasional', label: 'Occasional', caption: 'A gentle pulse. Once in a while an agent drops a genuinely relevant headline or thought to keep things fresh, but they will not chat among themselves. Always free.' },
];

type Domain = 'yours' | 'all' | 'media' | 'startup' | 'datascience' | 'study' | 'creative' | 'marketing' | 'research' | 'engineering' | 'markets' | 'lifestyle'
  | 'wellness' | 'finance' | 'legal' | 'career' | 'product' | 'cooking' | 'travel' | 'coaching';
type Step = 'category' | 'agents' | 'crew' | 'review';

/** Each category as a tile: an icon + label + a one-line caption (not a thin tab). */
const DOMAIN_META: Record<Domain, { label: string; caption: string; Icon: React.FC<{ className?: string }> }> = {
  yours: { label: 'Your own agents', caption: 'Specialists you built yourself', Icon: Sparkles },
  all: { label: 'All specialists', caption: 'Browse the entire roster', Icon: LayoutGrid },
  media: { label: 'Your production crew', caption: 'Directors, composers, art, voice', Icon: Clapperboard },
  startup: { label: 'Startup & Founders', caption: 'GTM, fundraising, unit economics', Icon: Rocket },
  datascience: { label: 'Data Science & AI', caption: 'Modelling, statistics, ML rigor', Icon: LineChart },
  study: { label: 'Study & Learning', caption: 'Tutors and study planners', Icon: GraduationCap },
  creative: { label: 'Writers room', caption: 'Story, worldbuilding, plot, dialogue', Icon: BookOpen },
  marketing: { label: 'Marketing', caption: 'Strategy, copy and campaigns', Icon: Megaphone },
  research: { label: 'Research & Consulting', caption: 'Evidence, frameworks, synthesis', Icon: Search },
  engineering: { label: 'Engineering', caption: 'Architecture, code and systems', Icon: Code },
  markets: { label: 'Markets & Macro', caption: 'Rates, prediction markets, risk', Icon: TrendingUp },
  lifestyle: { label: 'Dating & Social', caption: 'Conversation, relationships, social', Icon: Heart },
  wellness: { label: 'Health & Wellness', caption: 'Fitness, nutrition, habits & balance', Icon: HeartPulse },
  finance: { label: 'Personal Finance', caption: 'Budgeting, saving & investing basics', Icon: Wallet },
  legal: { label: 'Rights & clearances', caption: 'Copyright, likeness, music licensing', Icon: Scale },
  career: { label: 'Career & Job Search', caption: 'Resume, interviews & networking', Icon: Briefcase },
  product: { label: 'Product & Design', caption: 'PM, UX/UI & user research', Icon: Kanban },
  cooking: { label: 'Cooking & Home', caption: 'Recipes, meal plans & organising', Icon: ChefHat },
  travel: { label: 'Travel', caption: 'Trips, itineraries & local tips', Icon: Plane },
  coaching: { label: 'Coaching & Productivity', caption: 'Goals, focus & mindfulness', Icon: Compass },
};
/**
 * RF1 — the categories a STORYTELLING creator needs, and only those.
 *
 * This grid used to list 19 verticals (Cooking & Home, Dating & Social, Travel, Personal Finance, Career…) —
 * the general-assistant product, not ours. A creator assembling a micro-drama crew had to navigate past fifteen
 * irrelevant intents to reach the four that matter. These are the production disciplines: the media crew, the
 * writers' room, the rights advisor, plus your own agents.
 */
const PRODUCTION_DOMAINS: Domain[] = ['media', 'creative', 'legal'];

/**
 * Everything else. KEPT, not deleted (the de-emphasis rule — those agents stay in the product), but folded
 * behind one quiet "More topics" disclosure so it costs a creator nothing to ignore.
 */
/* 'yours' (custom agents) is deliberately in NEITHER list (owner 2026-08-16, screenshot83: "remove Your own
   agents for now") — so it is unreachable rather than deleted. Its render branches below are intact, so
   restoring it is a one-word change when custom agents get their own home. */
const OTHER_DOMAINS: Domain[] = [
  'startup', 'markets', 'marketing', 'product', 'research', 'datascience', 'engineering',
  'career', 'finance', 'wellness', 'coaching', 'study', 'cooking', 'travel', 'lifestyle',
  'all',
];

/** Map a persistent custom agent into a conversation member (links knowledge/tools). */
const customToMember = (a: CustomAgent): MemberSpec => ({
  id: a.id, name: a.name, persona: a.persona, avatar_icon: a.avatar_icon,
  profile_id: null, custom_agent_id: a.id, system_prompt: a.system_prompt,
  model_tier: a.model_tier, temperature: a.temperature, tools: a.tools, knowledge_backend: a.knowledge_backend,
});

// Built-in roster shown if the authed profiles call is unavailable. Domain-tagged across ALL
// categories so no category is ever empty on fallback (mirrors each catalog pack's flagships).
const FALLBACK: Specialist[] = [
  { id: 'macro-analyst', name: 'Magnus', persona: 'Macro Analyst', description: 'Reads macro conditions — rates, cycles, policy.', avatar_icon: 'globe', model_tier: 'EXPERT', temperature: 0.3, capabilities: ['macro', 'rates'], domain: 'markets', isDefault: true },
  { id: 'markets-analyst', name: 'Prism', persona: 'Markets & Prediction Analyst', description: 'Prices probability via prediction markets.', avatar_icon: 'activity', model_tier: 'EXPERT', temperature: 0.3, capabilities: ['prediction markets', 'polymarket'], domain: 'markets', isDefault: true },
  { id: 'contrarian', name: 'Vex', persona: 'Contrarian & Risk', description: 'Stress-tests every thesis.', avatar_icon: 'shield-alert', model_tier: 'SMART', temperature: 0.5, capabilities: ['contrarian', 'risk'], domain: 'markets', isDefault: false },
  { id: 'synthesizer', name: 'Lens', persona: 'Research Synthesizer', description: 'Turns debate into a decision-ready brief.', avatar_icon: 'file-text', model_tier: 'SMART', temperature: 0.4, capabilities: ['synthesis', 'writing'], domain: 'markets', isDefault: false },
  { id: 'worldbuilder', name: 'Lore', persona: 'Worldbuilder', description: 'Builds consistent universes — physics, history, cultures.', avatar_icon: 'map', model_tier: 'SMART', temperature: 0.7, capabilities: ['worldbuilding', 'lore'], domain: 'creative', isDefault: false },
  { id: 'plot-architect', name: 'Arc', persona: 'Plot Architect', description: 'Structures story — acts, tension, reversals.', avatar_icon: 'layout', model_tier: 'SMART', temperature: 0.6, capabilities: ['plot', 'structure'], domain: 'creative', isDefault: false },
  { id: 'story-editor', name: 'Crisp', persona: 'Story Editor & Critic', description: 'Reads as a demanding editor; earns the ending.', avatar_icon: 'edit-3', model_tier: 'SMART', temperature: 0.5, capabilities: ['editing', 'critique'], domain: 'creative', isDefault: false },
  { id: 'strategist', name: 'Mira', persona: 'Marketing Strategist', description: 'Owns the brief and campaign plan.', avatar_icon: 'target', model_tier: 'SMART', temperature: 0.4, capabilities: ['positioning', 'strategy'], domain: 'marketing', isDefault: false },
  { id: 'copywriter', name: 'Quill', persona: 'Copywriter', description: 'Headlines, hooks, and on-brand copy.', avatar_icon: 'pen-tool', model_tier: 'SMART', temperature: 0.7, capabilities: ['copywriting', 'messaging'], domain: 'marketing', isDefault: false },
  { id: 'researcher', name: 'Scout', persona: 'Research Analyst', description: 'Gathers + synthesizes evidence, weighs sources.', avatar_icon: 'search', model_tier: 'RESEARCH', temperature: 0.3, capabilities: ['research', 'synthesis'], domain: 'research', isDefault: false },
  { id: 'consultant', name: 'Frame', persona: 'Strategy Consultant', description: 'Issue trees, hypotheses, recommendations.', avatar_icon: 'briefcase', model_tier: 'SMART', temperature: 0.4, capabilities: ['frameworks', 'strategy'], domain: 'research', isDefault: false },
  { id: 'gtm-strategist', name: 'Hook', persona: 'Go-to-Market Strategist', description: 'Finds the wedge — ICP, message, channel.', avatar_icon: 'crosshair', model_tier: 'SMART', temperature: 0.5, capabilities: ['gtm', 'positioning'], domain: 'startup', isDefault: false },
  { id: 'startup-skeptic', name: 'Grill', persona: 'Skeptical Investor', description: 'Tears into the model — unit economics, moat.', avatar_icon: 'flame', model_tier: 'SMART', temperature: 0.5, capabilities: ['unit economics', 'risk'], domain: 'startup', isDefault: false },
  { id: 'data-scientist', name: 'Nova', persona: 'Data Scientist', description: 'Frames the question as data — models, validation.', avatar_icon: 'line-chart', model_tier: 'EXPERT', temperature: 0.3, capabilities: ['modelling', 'eda'], domain: 'datascience', isDefault: false },
  { id: 'statistician', name: 'Bayes', persona: 'Statistician', description: 'Guards the rigor — significance, causation.', avatar_icon: 'sigma', model_tier: 'EXPERT', temperature: 0.2, capabilities: ['statistics', 'causal inference'], domain: 'datascience', isDefault: false },
  { id: 'tutor', name: 'Sage', persona: 'Tutor & Explainer', description: 'Explains anything simply — analogies, worked examples.', avatar_icon: 'graduation-cap', model_tier: 'SMART', temperature: 0.6, capabilities: ['tutoring', 'explanation'], domain: 'study', isDefault: false },
  { id: 'study-planner', name: 'Cadence', persona: 'Study Planner', description: 'A realistic plan — spaced repetition, milestones.', avatar_icon: 'calendar-clock', model_tier: 'SMART', temperature: 0.4, capabilities: ['study planning', 'scheduling'], domain: 'study', isDefault: false },
  { id: 'video-director', name: 'Reel', persona: 'Video Director', description: 'Shot-listed 30s+ video — cinematography, pacing, social framing.', avatar_icon: 'clapperboard', model_tier: 'SMART', temperature: 0.6, capabilities: ['video direction', 'cinematography'], domain: 'media', isDefault: false },
  { id: 'image-artist', name: 'Canvas', persona: 'Image Artist & Art Director', description: 'Striking stills + art direction — composition, palette.', avatar_icon: 'image', model_tier: 'SMART', temperature: 0.6, capabilities: ['image generation', 'art direction'], domain: 'media', isDefault: false },
  { id: 'photographer', name: 'Lux', persona: 'Photographer & Photo Editor', description: 'Assess + improve your photos; declutter messy rooms.', avatar_icon: 'camera', model_tier: 'SMART', temperature: 0.5, capabilities: ['photo critique', 'editing'], domain: 'media', isDefault: false },
  { id: 'music-composer', name: 'Tempo', persona: 'Music Composer', description: 'Scores the moment — genre, mood, tempo.', avatar_icon: 'music', model_tier: 'SMART', temperature: 0.6, capabilities: ['music generation', 'scoring'], domain: 'media', isDefault: false },
  { id: 'voiceover-artist', name: 'Verse', persona: 'Voiceover & Narration', description: 'Writes + voices narration for ads, explainers, reels.', avatar_icon: 'mic', model_tier: 'SMART', temperature: 0.5, capabilities: ['voiceover', 'narration'], domain: 'media', isDefault: false },
  // New verticals — one flagship each so no category is empty on fallback.
  { id: 'wellness-coach', name: 'Vita', persona: 'Wellness Coach', description: 'Builds sustainable healthy habits — sleep, stress, movement, balance.', avatar_icon: 'heart-pulse', model_tier: 'SMART', temperature: 0.5, capabilities: ['habits', 'wellbeing'], domain: 'wellness', isDefault: false },
  { id: 'budgeting-coach', name: 'Ledger', persona: 'Budgeting Coach', description: 'Budgets, cash flow, and spending that actually sticks.', avatar_icon: 'wallet', model_tier: 'SMART', temperature: 0.4, capabilities: ['budgeting', 'saving'], domain: 'finance', isDefault: false },
  { id: 'contracts-explainer', name: 'Clause', persona: 'Contracts Explainer', description: 'Plain-English breakdowns of terms, risks, and red flags.', avatar_icon: 'file-signature', model_tier: 'SMART', temperature: 0.3, capabilities: ['contracts', 'legal info'], domain: 'legal', isDefault: false },
  { id: 'resume-coach', name: 'Pitch', persona: 'Resume & Profile Coach', description: 'Impact bullets, keywords, and a clear career story.', avatar_icon: 'contact', model_tier: 'SMART', temperature: 0.5, capabilities: ['resume', 'linkedin'], domain: 'career', isDefault: false },
  { id: 'product-manager', name: 'Pivot', persona: 'Product Manager', description: 'Problems, priorities, specs, and crisp tradeoffs.', avatar_icon: 'kanban', model_tier: 'SMART', temperature: 0.4, capabilities: ['product', 'prioritization'], domain: 'product', isDefault: false },
  { id: 'recipe-chef', name: 'Basil', persona: 'Recipe Chef', description: 'Recipes, swaps, and technique from what you have.', avatar_icon: 'chef-hat', model_tier: 'SMART', temperature: 0.6, capabilities: ['recipes', 'technique'], domain: 'cooking', isDefault: false },
  { id: 'travel-planner', name: 'Pilot', persona: 'Travel Planner', description: 'Destinations, routing, and a realistic budget.', avatar_icon: 'plane', model_tier: 'SMART', temperature: 0.5, capabilities: ['trip planning', 'budgeting'], domain: 'travel', isDefault: false },
  { id: 'productivity-coach', name: 'Momentum', persona: 'Productivity Coach', description: 'Focus, systems, and a plan that beats overwhelm.', avatar_icon: 'list-checks', model_tier: 'SMART', temperature: 0.5, capabilities: ['productivity', 'focus'], domain: 'coaching', isDefault: false },
];

/** What the full-profile preview needs — resolved from a catalog specialist or a custom agent. */
interface PreviewTarget { member: MemberSpec; profileId?: string | null; name: string; persona: string; description?: string | null; avatarIcon?: string | null; avatarUrl?: string | null }

const NewChatPicker: React.FC<Props> = ({ open, onClose, onCreate }) => {
  const catalogAvatars = useCatalogAvatars(); // admin default avatars, keyed by profile_id (= specialist id)
  const isDesktop = useIsDesktop();
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [step, setStep] = useState<Step>('category');
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [selected, setSelected] = useState<MemberSpec[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', instructions: '' });
  const [groupName, setGroupName] = useState('');
  const [proactiveLevel, setProactiveLevel] = useState<ProactiveLevel>('none');
  const [domain, setDomain] = useState<Domain>('all');
  const [topicInput, setTopicInput] = useState('');
  // RF1 — the non-production categories stay collapsed. Default closed: our creator should never see them.
  const [showMore, setShowMore] = useState(false);
  const [customAgents, setCustomAgents] = useState<CustomAgent[]>([]);
  const { planTier } = usePlan();
  // Free users get 3 custom agents (KB upload allowed for all — usage spends credits, nudging an upgrade).
  const agentCap = planTier === 'FREE' ? 3 : 25;
  const atAgentCap = customAgents.length >= agentCap;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<CustomAgent | null>(null);

  const refreshCustom = () => { listCustomAgents().then(setCustomAgents).catch(() => setCustomAgents([])); };

  useEffect(() => {
    if (!open) return;
    setStep('category'); setSelected([]); setCreating(false); setForm({ name: '', role: '', instructions: '' });
    setGroupName(''); setProactiveLevel('none'); setDomain('all'); setTopicInput(''); setPreview(null);
    getAgentProfiles()
      .then((list) => setSpecialists(list && list.length ? list : FALLBACK))
      .catch(() => setSpecialists(FALLBACK));
    refreshCustom();
  }, [open]);

  if (!open) return null;

  const visible = domain === 'all' ? specialists : specialists.filter((s) => (s as { domain?: string }).domain === domain);
  const has = (id: string) => selected.some((m) => m.id === id);
  const toggleSpec = (m: MemberSpec) => {
    setSelected((prev) => prev.some((x) => x.id === m.id)
      ? prev.filter((x) => x.id !== m.id)
      : prev.length < GROUP_MAX ? [...prev, m] : prev);
  };
  const toggle = (s: Specialist) => toggleSpec(toMember(s));

  const applySuggestion = () => {
    if (!topicInput.trim()) return;
    setSelected(suggestTeam(specialists, topicInput.trim()));
    setTopicInput('');
    setStep('review');
  };

  // Staff the chat from the Media-crew picker: map the resolved catalog agent ids → members (using the loaded
  // specialist catalog for name/persona/avatar), cap at GROUP_MAX, then jump to review.
  const applyCrew = (agentIds: string[]) => {
    const members = agentIds
      .map((id) => specialists.find((s) => s.id === id))
      .filter((s): s is Specialist => !!s)
      .slice(0, GROUP_MAX)
      .map(toMember);
    if (members.length) { setSelected(members); setStep('review'); }
  };

  const addCustom = () => {
    const name = form.name.trim();
    if (!name || selected.length >= GROUP_MAX) return;
    const id = `custom-${name.toLowerCase().replace(/\s+/g, '-')}-${selected.length}`;
    setSelected((prev) => [...prev, {
      id, name, persona: form.role.trim() || 'Assistant', avatar_icon: 'sparkles',
      profile_id: null, system_prompt: form.instructions.trim() || `You are ${name}, a helpful ${form.role.trim() || 'assistant'}.`,
      model_tier: 'SMART', temperature: 0.6,
    }]);
    setForm({ name: '', role: '', instructions: '' });
    setCreating(false);
  };

  const openEditor = (a: CustomAgent | null) => { setEditingAgent(a); setEditorOpen(true); };
  const goAgents = (d: Domain) => { setDomain(d); setCreating(false); setStep('agents'); };

  const headerText = step === 'category'
    ? { title: 'Build your group', sub: 'Describe your goal, or browse by category.' }
    : step === 'agents'
      ? { title: DOMAIN_META[domain].label, sub: DOMAIN_META[domain].caption }
      : step === 'crew'
        ? { title: 'Assemble a media crew', sub: 'Pick a flavor per role, or start from a ready-made crew.' }
        : { title: 'Name & start', sub: 'Review your group, give it a name, and start the chat.' };

  return (
    <div className={`${flowOverlay} z-50`}>
      <div className="absolute inset-0 hidden sm:block" onClick={onClose} />
      <div className={`${flowPanel} sm:max-w-lg sm:h-[620px] sm:max-h-[90dvh] relative`} onClick={(e) => e.stopPropagation()}>

        {/* Header — a Back affordance on inner steps keeps the flow obvious */}
        <div className={modalHeader}>
          <div className="flex items-center gap-2 min-w-0">
            {step !== 'category' && (
              <button onClick={() => setStep('category')} aria-label="Back to categories" title="Back" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white shrink-0"><ArrowLeft className="w-4 h-4" /></button>
            )}
            <div className="min-w-0">
              <h3 className={modalTitle}>{headerText.title}</h3>
              <p className={`${TYPE.control} text-secondary-500 truncate`}>{headerText.sub}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" title="Close" className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {/* Step progress — segmented, monochrome. Category -> Pick -> Review. */}
        <div className="flex items-center gap-1.5 px-6 pt-4 shrink-0">
          {(['category', 'agents', 'review'] as Step[]).map((s, i) => {
            const idx = step === 'category' ? 0 : step === 'agents' || step === 'crew' ? 1 : 2;
            return <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${i <= idx ? 'bg-secondary-900 dark:bg-white' : 'bg-secondary-200 dark:bg-secondary-700'}`} />;
          })}
        </div>

        {/* ─────────── STEP 1 · CATEGORY ─────────── */}
        {step === 'category' && (
          <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6 min-h-0">
            {/* RF1 — THE PRIMARY PATH for our creator: a ready-made production crew, one tap. This used to sit
                below a generic "what are you working on?" box; for someone making an episode, staffing the
                production IS the job, so it leads. */}
            <button onClick={() => setStep('crew')}
              className="flex w-full items-center gap-3 rounded-xl border-2 border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800 p-4 text-left transition-colors hover:bg-secondary-100 dark:hover:bg-secondary-700">
              <Clapperboard className="h-5 w-5 shrink-0 text-secondary-900 dark:text-white" />
              <span className="min-w-0 flex-1">
                <span className={`block ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>Assemble a production crew</span>
                <span className={`block ${TYPE.control} text-secondary-500 leading-snug dark:text-secondary-400`}>A director, composer, writer and rights advisor — or start from a ready-made crew.</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-secondary-400" />
            </button>

            {/* The production disciplines — four, not nineteen. */}
            <div className="grid grid-cols-2 gap-3">
              {PRODUCTION_DOMAINS.map((d) => {
                const { label, caption, Icon } = DOMAIN_META[d];
                return (
                  <button key={d} onClick={() => goAgents(d)}
                    className="text-left rounded-xl border border-secondary-200 dark:border-secondary-600 p-4 hover:border-secondary-400 dark:hover:border-secondary-500 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors">
                    <Icon className="w-5 h-5 text-secondary-700 dark:text-secondary-200 mb-2" />
                    <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{label}</p>
                    <p className={`mt-0.5 ${TYPE.control} text-secondary-500 leading-snug`}>{caption}</p>
                  </button>
                );
              })}
              {/* MORE TOPICS is a TILE now (owner 2026-08-16, screenshot83), taking the slot "Your own agents"
                  vacated — a fourth peer in the grid rather than a 11px link buried under it, which nobody
                  found. Custom agents are hidden for now; they return with their own home. */}
              <button
                onClick={() => setShowMore((v) => !v)}
                aria-expanded={showMore}
                data-testid="more-topics"
                className="text-left rounded-xl border border-dashed border-secondary-300 dark:border-secondary-600 p-4 hover:border-secondary-400 dark:hover:border-secondary-500 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors">
                <LayoutGrid className="w-5 h-5 text-secondary-500 mb-2" />
                <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>
                  {showMore ? 'Hide other topics' : 'More topics'}
                </p>
                <p className={`mt-0.5 ${TYPE.control} text-secondary-500 leading-snug`}>Everything outside the production crew</p>
              </button>
            </div>

            {/* Everything else — kept, quiet, and one tap away. A creator never has to look at it. */}
            <div>
              {showMore && (
                <>
                  {/* The generic team-builder lives here too — useful, but not the creator's path. */}
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-secondary-200 px-3 py-2.5 dark:border-secondary-600">
                    <Sparkles className="w-4 h-4 shrink-0 text-secondary-400" />
                    <input
                      value={topicInput}
                      onChange={(e) => setTopicInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') applySuggestion(); }}
                      placeholder="Describe any other topic and we'll staff it"
                      className={`flex-1 bg-transparent ${TYPE.body} text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:outline-none`}
                    />
                    <button onClick={applySuggestion} disabled={!topicInput.trim()}
                      className={`shrink-0 rounded-lg bg-secondary-900 px-3 py-1.5 ${TYPE.control} font-semibold text-white disabled:opacity-40 dark:bg-white dark:text-secondary-900`}>
                      Build
                    </button>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {OTHER_DOMAINS.map((d) => {
                      const { label, caption, Icon } = DOMAIN_META[d];
                      return (
                        <button key={d} onClick={() => goAgents(d)}
                          className="text-left rounded-xl border border-secondary-200 dark:border-secondary-600 p-4 hover:border-secondary-400 dark:hover:border-secondary-500 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors">
                          <Icon className="w-5 h-5 text-secondary-700 dark:text-secondary-200 mb-2" />
                          <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{label}</p>
                          <p className={`mt-0.5 ${TYPE.control} text-secondary-500 leading-snug`}>{caption}</p>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ─────────── STEP · MEDIA CREW ─────────── */}
        {step === 'crew' && (
          <div className="overflow-y-auto flex-1 px-6 py-6 min-h-0">
            <MediaCrewPicker onPick={applyCrew} />
          </div>
        )}

        {/* ─────────── STEP 2 · AGENTS in the chosen category ─────────── */}
        {step === 'agents' && (
          <div className="overflow-y-auto flex-1 px-4 py-5 space-y-2 min-h-0">
            {domain === 'yours' && (
              <>
                <button onClick={() => openEditor(null)} disabled={atAgentCap}
                  className={`w-full flex items-center gap-2 px-3 py-3 rounded-xl ${TYPE.body} text-secondary-600 dark:text-secondary-300 border border-dashed border-secondary-300 dark:border-secondary-600 hover:border-secondary-500 dark:hover:border-secondary-500 disabled:opacity-40 disabled:hover:border-secondary-300`}>
                  <Plus className="w-4 h-4" /> Create an agent{customAgents.length > 0 ? ` · ${customAgents.length}/${agentCap}` : ''}
                </button>
                {atAgentCap && (
                  <p className={`${TYPE.control} text-center text-secondary-500`}>
                    {planTier === 'FREE'
                      ? <>Free plan includes {agentCap} custom agents. <Link to="/pricing" className="underline hover:text-secondary-900 dark:hover:text-white">Subscribe</Link> for more.</>
                      : <>You&apos;ve reached the {agentCap} custom-agent limit.</>}
                  </p>
                )}
                {customAgents.length === 0 && (
                  <p className={`${TYPE.body} text-secondary-400 text-center py-6`}>No custom agents yet — create one with its own instructions and knowledge.</p>
                )}
                {customAgents.map((a) => {
                  const m = customToMember(a);
                  return (
                    <div key={a.id} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${has(a.id) ? 'bg-secondary-100 dark:bg-secondary-800' : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/50'}`}>
                      <button onClick={() => toggleSpec(m)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        <div className="w-9 h-9 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center shrink-0 overflow-hidden">
                          {a.avatar_url ? <img src={a.avatar_url} alt="" className="w-full h-full object-cover" /> : <SpecialistIcon name={a.avatar_icon} className="w-5 h-5 text-secondary-600 dark:text-secondary-300" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white truncate`}>{a.name} <span className="font-normal text-secondary-400">· {a.persona}</span></p>
                          <p className={`${TYPE.control} text-secondary-500 truncate`}>{a.description || 'Custom agent'}</p>
                        </div>
                        {has(a.id) && <Check className="w-4 h-4 text-secondary-900 dark:text-white shrink-0" />}
                      </button>
                      <button onClick={() => setPreview({ member: m, profileId: null, name: a.name, persona: a.persona, description: a.description || 'Custom agent', avatarIcon: a.avatar_icon, avatarUrl: a.avatar_url || null })}
                        aria-label={`View ${a.name}'s full profile`} title="View full profile" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 shrink-0"><Info className="w-4 h-4" /></button>
                      <button onClick={() => openEditor(a)} aria-label={`Edit ${a.name}`} title={`Edit ${a.name}`} className="text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 shrink-0"><Pencil className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </>
            )}

            {domain !== 'yours' && visible.map((s) => (
              <div key={s.id}
                className={`w-full flex items-center gap-2 px-3 py-3 rounded-xl transition-colors ${has(s.id) ? 'bg-secondary-100 dark:bg-secondary-800' : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/50'}`}>
                <button onClick={() => toggle(s)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <div className="w-9 h-9 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center shrink-0 overflow-hidden">
                    {catalogAvatars[s.id]
                      ? <img src={catalogAvatars[s.id]} alt="" className="w-full h-full object-cover" />
                      : <SpecialistIcon name={s.avatar_icon} className="w-5 h-5 text-secondary-600 dark:text-secondary-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{s.name} <span className="font-normal text-secondary-400">· {s.persona}</span></p>
                    <p className={`${TYPE.control} text-secondary-500 truncate`}>{s.description}</p>
                  </div>
                  {has(s.id) && <Check className="w-4 h-4 text-secondary-900 dark:text-white shrink-0" />}
                  {selected.length >= GROUP_MAX && !has(s.id) && <span className={`${TYPE.control} text-secondary-300 shrink-0`}>Max {GROUP_MAX}</span>}
                </button>
                <button onClick={() => setPreview({ member: toMember(s), profileId: s.id, name: s.name, persona: s.persona, description: s.description, avatarIcon: s.avatar_icon, avatarUrl: catalogAvatars[s.id] || null })}
                  aria-label={`View ${s.name}'s full profile`} title="View full profile"
                  className="shrink-0 text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200"><Info className="w-4 h-4" /></button>
              </div>
            ))}

            {domain !== 'yours' && visible.length === 0 && (
              <p className={`${TYPE.body} text-secondary-400 text-center py-8`}>No agents in this category yet.</p>
            )}

            {/* Create-your-own (ad-hoc, this chat only) — ONLY on the default-category steps. The "Your agents"
                step already has its own "Create an agent" (full editor) CTA above, so showing this there too
                duplicated the create action (screenshot42). */}
            {domain !== 'yours' && (creating ? (
              <div className="p-4 rounded-xl border border-secondary-200 dark:border-secondary-600 space-y-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name (e.g. Lex)" className={modalField} />
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role (e.g. Legal advisor)" className={modalField} />
                <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3} placeholder="What should they focus on? (optional)" className={`${modalField} resize-none`} />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setCreating(false)} className={`px-3 py-1.5 ${TYPE.body} text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200`}>Cancel</button>
                  <button onClick={addCustom} disabled={!form.name.trim()} className={`px-3 py-1.5 ${TYPE.body} rounded-lg bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 font-medium disabled:opacity-40`}>Add</button>
                </div>
                {/* Route to the full editor for a PERSISTENT agent you can give a knowledge base (docs, URLs, pasted text). */}
                <button onClick={() => { setCreating(false); openEditor(null); }}
                  className={`w-full text-left ${TYPE.control} text-secondary-500 hover:text-secondary-900 dark:hover:text-white`}>
                  Want to attach documents, reference URLs or paste text? <span className="underline">Create a full agent with a knowledge base →</span>
                </button>
              </div>
            ) : (
              <button onClick={() => setCreating(true)} disabled={selected.length >= GROUP_MAX}
                className={`w-full flex items-center gap-2 px-3 py-3 rounded-xl ${TYPE.body} text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 disabled:opacity-40`}>
                <UserPlus className="w-4 h-4" /> Create your own agent
              </button>
            ))}
          </div>
        )}

        {/* ─────────── STEP 3 · REVIEW & NAME ─────────── */}
        {step === 'review' && (
          <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6 min-h-0">
            <div>
              <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Your group · {selected.length}/{GROUP_MAX}</p>
              {selected.length === 0 ? (
                <p className={`${TYPE.body} text-secondary-400 py-6 text-center`}>No one selected yet — go back and pick a few specialists.</p>
              ) : (
                <div className="space-y-2">
                  {selected.map((m) => (
                    <div key={m.id} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-secondary-100 dark:bg-secondary-800">
                      <div className="w-8 h-8 rounded-lg bg-secondary-200 dark:bg-secondary-700 flex items-center justify-center shrink-0 overflow-hidden">
                        {m.profile_id && catalogAvatars[m.profile_id]
                          ? <img src={catalogAvatars[m.profile_id]} alt="" className="w-full h-full object-cover" />
                          : <SpecialistIcon name={m.avatar_icon} className="w-4 h-4 text-secondary-600 dark:text-secondary-300" />}
                      </div>
                      <p className={`flex-1 min-w-0 truncate ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{m.name} <span className="font-normal text-secondary-400">· {m.persona}</span></p>
                      <button onClick={() => setSelected((prev) => prev.filter((x) => x.id !== m.id))} aria-label={`Remove ${m.name}`} className="text-secondary-400 hover:text-secondary-500 shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setStep('category')} className={`mt-3 inline-flex items-center gap-1.5 ${TYPE.control} text-secondary-500 hover:text-secondary-900 dark:hover:text-white`}>
                <Plus className="w-3.5 h-3.5" /> Add more from a category
              </button>
            </div>

            <div>
              <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Group logo</p>
              <div className="flex items-center gap-3">
                <GroupAvatar name={groupName.trim() || 'New group'} className={`w-10 h-10 ${TYPE.body}`} />
                <p className={`${TYPE.control} text-secondary-500`}>A coloured initial is assigned automatically — you can set a custom logo later in group settings.</p>
              </div>
            </div>

            <div>
              <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Group name</p>
              <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Optional — e.g. Launch team" className={modalField} />
            </div>

            <div>
              <p className={`${TYPE.label} text-secondary-400`}>Proactive interaction</p>
              <p className={`mb-2 mt-0.5 ${TYPE.control} text-secondary-500`}>How lively the group feels when you are not typing — how often agents surface news and talk among themselves. You can change this any time in group settings.</p>
              <div className="space-y-2">
                {PROACTIVE_OPTIONS.map((o) => (
                  <button key={o.key} onClick={() => setProactiveLevel(o.key)}
                    className={`w-full text-left rounded-xl border p-3 transition-colors ${proactiveLevel === o.key ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800' : 'border-secondary-200 dark:border-secondary-600 hover:border-secondary-400 dark:hover:border-secondary-500'}`}>
                    <div className="flex items-center justify-between">
                      <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{o.label}</p>
                      {proactiveLevel === o.key && <Check className="w-4 h-4 text-secondary-900 dark:text-white" />}
                    </div>
                    <p className={`mt-0.5 ${TYPE.control} text-secondary-500 leading-snug`}>{o.caption}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer — step-aware actions. Hidden on the crew step (the crew picker owns its own "Add crew" CTA). */}
        {step !== 'crew' && (
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-secondary-200 dark:border-secondary-600 shrink-0">
          <span className={`${TYPE.body} text-secondary-500`}>
            {selected.length}/{GROUP_MAX} selected
            {selected.length > 0 && <button onClick={() => setSelected([])} className={`ml-2 ${TYPE.control} text-secondary-400 hover:text-secondary-500`}>Clear</button>}
          </span>
          {step === 'review' ? (
            <button
              onClick={() => selected.length >= GROUP_MIN && onCreate(selected, groupName.trim() || undefined, proactiveLevel)}
              disabled={selected.length < GROUP_MIN}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 ${TYPE.sectionTitle} disabled:opacity-40 hover:bg-black dark:hover:bg-secondary-100 transition-colors`}>
              <Check className="w-4 h-4" /> Build your group
            </button>
          ) : (
            <button
              onClick={() => setStep('review')}
              disabled={selected.length < GROUP_MIN}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 ${TYPE.sectionTitle} disabled:opacity-40 hover:bg-black dark:hover:bg-secondary-100 transition-colors`}>
              Review <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
        )}
      </div>

      <AgentEditorModal
        open={editorOpen}
        agent={editingAgent}
        onClose={() => setEditorOpen(false)}
        onSaved={(a) => {
          refreshCustom();
          // A freshly-created full agent (with its knowledge base) is added straight to the roster.
          setSelected((prev) => (a && prev.length < GROUP_MAX && !prev.some((s) => s.custom_agent_id === a.id) ? [...prev, customToMember(a)] : prev));
        }}
      />

      {preview && (
        <AgentPreviewCard
          open={!!preview}
          asPage={!isDesktop}
          profileId={preview.profileId}
          name={preview.name}
          persona={preview.persona}
          description={preview.description}
          avatarIcon={preview.avatarIcon}
          avatarUrl={preview.avatarUrl}
          selected={has(preview.member.id)}
          canAdd={has(preview.member.id) || selected.length < GROUP_MAX}
          onToggle={() => toggleSpec(preview.member)}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default NewChatPicker;
